
import { defineComponent, ref, onMounted, computed } from "vue";
import TableSummary from "../../components/table-summary/index.vue";
import TransferPickupModal from "../../courier/transfer-pickup/component/modal.vue";
import Icons from "@/app/ui/components/icons/index.vue";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import {
  RequestListSummary,
  SummaryDataList,
  ListSummaryItem,
} from "@/domain/entities/PickupDashboardSummary";

interface SelectedSellerData {
  id: string;
  sellerName: string;
  sellerPhone: string;
  packageCount: number;
  koli: number;
  weight: number;
  taskGroupId?: number;
  index: number;
}

export default defineComponent({
  name: "PickupDashboardMonitoringCRROTW",

  components: {
    TableSummary,
    TransferPickupModal,
    Icons,
  },

  setup() {
    const listSummaryData = ref<SummaryDataList>(new SummaryDataList());

    const page = ref(1);
    const limit = ref(20);

    const sortKey = ref<string | null>("total_shipment");
    const sortOrder = ref<"asc" | "desc" | null>("desc");

    const showTransferModal = ref(false);
    const selectedSellers = ref<SelectedSellerData[]>([]);
    const sourceCourierId = ref(0);
    const adjustHeight = ref<string>("");

    const columns = [
      {
        key: "no",
        label: "pickupDashboard.column.no",
        style: { width: "60px", textAlign: "center" }
      },
      {
        key: "senderName",
        label: "pickupDashboard.column.senderName",
        sortable: true
      },
      {
        key: "courierName",
        label: "pickupDashboard.column.courierName",
        sortable: true
      },
      {
        key: "shipmentType",
        label: "pickupDashboard.column.shipmentType"
      },
      {
        key: "totalShipment",
        label: "pickupDashboard.column.totalShipment",
        sortable: true
      },
      {
        key: "expiredAt",
        label: "pickupDashboard.column.remainingTime",
        sortable: true
      },
      {
        key: "senderAddress",
        label: "pickupDashboard.column.address",
        style: { width: "280px" }
      },
      {
        key: "action",
        label: "pickupDashboard.column.transferPickUp",
        style: { width: "80px", textAlign: "center" }
      }
    ];

    const waNumber = (phone: string) => String(phone || "").replace(/\D/g, "");

    const detailSummary = computed(
      () => PickupDashboardController.detailSummary
    );

    const pagination = computed(() => PickupDashboardController.pagination);

    const startIndex = computed(
      () => (pagination.value.page - 1) * pagination.value.limit
    );

    const isLoading = computed(
      () => PickupDashboardController.loadingDetailSummary
    );

    const camelToSnake = (value: string): string => {
      return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    const fetchListSummary = async () => {
      PickupDashboardController.setFilter({
        status: "CRROTW",
      });

      const filterResult = PickupDashboardController.currentFilter;
      const startDate = filterResult.startDate || "";
      const endDate = filterResult.endDate || "";

      await PickupDashboardController.getListSummary(
        new RequestListSummary({
          q: filterResult.q,
          startDate: startDate,
          endDate: endDate,
          status: filterResult.status,
          shipmentType: filterResult.shipmentType,
          sortBy: sortOrder.value?.toUpperCase() ?? "",
          orderBy: camelToSnake(sortKey.value || filterResult.orderBy),
          page: page.value,
          limit: limit.value,
        })
      );
    };

    const handleSort = async ({
      key,
      order,
    }: {
      key: string;
      order: "asc" | "desc";
    }) => {
      sortKey.value = key;
      sortOrder.value = order;

      await fetchListSummary();
    };

    const handleSelectAll = (selectedItems: ListSummaryItem[]) => {
      PickupDashboardController.setTransferPickup(selectedItems);
    };

    const onSelectRow = (selectedItems: ListSummaryItem[]) => {
      PickupDashboardController.setTransferPickup(selectedItems);
    };

    const onPageChange = async (newPage: number) => {
      page.value = newPage;
      await fetchListSummary();
    };

    const openTransferModal = (item: ListSummaryItem) => {
      const uniqueId = item.incrementId.toString() || "0";

      selectedSellers.value = [
        {
          id: uniqueId,
          sellerName: item.senderName,
          sellerPhone: item.senderPhone,
          packageCount: item.totalShipment,
          koli: item.totalQuantity,
          weight: item.totalWeight,
          taskGroupId: item.taskId,
          index: 0,
        },
      ];
      sourceCourierId.value = item.courierId;
      showTransferModal.value = true;
    };

    const handleRemoveSeller = (sellerIndex: number) => {
      selectedSellers.value = selectedSellers.value.filter(
        (seller) => seller.index !== sellerIndex
      );
    };

    const handleTransferSuccess = async () => {
      // Reset selected sellers and close modal
      selectedSellers.value = [];
      showTransferModal.value = false;

      // Refresh list
      await fetchListSummary();
    };

    const fmtQty = (val: number | string) =>
      formatByCountry(Number(val || 0), { type: "quantity" });

    const mapsHref = (item: any) => {
      if (item.lat && item.lon) {
        return `https://www.google.com/maps?q=${item.lat},${item.lon}`;
      }
      const q = encodeURIComponent(item.sender_address || "");
      return `https://www.google.com/maps/search/?api=1&query=${q}`;
    };

    onMounted(async () => {
      await fetchListSummary();
    });

    return {
      columns,
      sortKey,
      sortOrder,
      listSummaryData,
      showTransferModal,
      selectedSellers,
      sourceCourierId,
      detailSummary,
      pagination,
      startIndex,
      isLoading,
      adjustHeight,
      mapsHref,
      fmtQty,
      handleSort,
      handleSelectAll,
      onSelectRow,
      onPageChange,
      openTransferModal,
      handleRemoveSeller,
      handleTransferSuccess,
      formatPhoneNumber,
      waNumber
    };
  }
});
